/**
 * Level-of-detail helpers: density aggregation over sorted beat arrays (W1-15 timeline-host; ARCHITECTURE.md §7.7).
 *
 * Columns keep their items sorted by beat (notes by time, keys by beat), so every query here is a binary search
 * (`Search` from core/math) and costs O(bins · log n), independent of how many items are visible:
 *   - `densityBins`: item counts per equal-height bin of a beat range (overview strip, density bands);
 *   - `planLod`: draw individual items while there is at most about one item per pixel row, density bands beyond
 *     (ARCHITECTURE.md §7.7 "more than one note per pixel row -> density bands");
 *   - `binMax`, `normalizeBins`: scaling for bar charts;
 *   - `minMaxOf`: min/max reduction of a slice (waveform peaks).
 * Beat arrays are any ArrayLike<number> sorted ascending (Float64Array, number[]); NaN entries sort last.
 */

import { Search } from '../../core/math/search';

/** Index range [first, end) of the items with beat0 <= beat <= beat1. */
export function indexRange(sorted: ArrayLike<number>, beat0: number, beat1: number): readonly [number, number] {
  const first = Search.lowerBound(sorted, beat0);
  const end = Search.upperBound(sorted, beat1, first);
  return [first, end];
}

/** Number of items with beat0 <= beat <= beat1. */
export function countInRange(sorted: ArrayLike<number>, beat0: number, beat1: number): number {
  const [first, end] = indexRange(sorted, beat0, beat1);
  return end - first;
}

/**
 * Counts per bin: bin i covers [beat0 + i·w, beat0 + (i+1)·w) with w = (beat1 - beat0) / bins; the last bin also
 * includes beat1. `out` is reused when it has the right length.
 */
export function densityBins(
  sorted: ArrayLike<number>,
  beat0: number,
  beat1: number,
  bins: number,
  out?: Float32Array,
): Float32Array {
  const n = Math.max(0, Math.floor(bins));
  const res = out !== undefined && out.length === n ? out : new Float32Array(n);
  res.fill(0);
  if (n === 0 || !(beat1 > beat0)) return res;
  const w = (beat1 - beat0) / n;
  let lo = Search.lowerBound(sorted, beat0);
  for (let i = 0; i < n; i++) {
    const end = i === n - 1 ? Search.upperBound(sorted, beat1, lo) : Search.lowerBound(sorted, beat0 + (i + 1) * w, lo);
    res[i] = end - lo;
    lo = end;
  }
  return res;
}

export function binMax(bins: ArrayLike<number>): number {
  let m = 0;
  for (let i = 0; i < bins.length; i++) {
    const v = bins[i] ?? 0;
    if (v > m) m = v;
  }
  return m;
}

/** Scales counts to 0..1 by the largest bin (all zero when empty). */
export function normalizeBins(bins: ArrayLike<number>, out?: Float32Array): Float32Array {
  const res = out !== undefined && out.length === bins.length ? out : new Float32Array(bins.length);
  const m = binMax(bins);
  for (let i = 0; i < bins.length; i++) res[i] = m > 0 ? (bins[i] ?? 0) / m : 0;
  return res;
}

/** Density bands of a crowded range (see `planLod`). */
export interface LodBands {
  /** Item counts per bin; bin i covers [beat0 + i·binBeats, beat0 + (i+1)·binBeats). */
  readonly bins: Float32Array;
  readonly beat0: number;
  readonly binBeats: number;
  /** Bin height in CSS px. */
  readonly binPx: number;
  /** 1 where the bin holds more than `threshold` items per pixel row: draw a band there instead of its items. */
  readonly crowded: Uint8Array;
  /** Items per pixel row of the densest bin. */
  readonly peakPerRow: number;
}

export interface LodPlan {
  /** Visible items are [first, end). */
  readonly first: number;
  readonly end: number;
  /** Null when every visible item can be drawn individually. */
  readonly bands: LodBands | null;
}

export interface LodOptions {
  /** Height of a density band in CSS px (default 4). */
  readonly binPx?: number;
  /** Items per pixel row above which a bin becomes a band (default 1). */
  readonly threshold?: number;
}

/**
 * Plans the drawing of the visible beat range [beat0, beat1] over `heightPx` CSS px: items are drawn one by one
 * except in bins that hold more than `threshold` items per pixel row, which become density bands. A sparse range
 * costs one binary search; a crowded one O(bins · log n).
 */
export function planLod(
  sorted: ArrayLike<number>,
  beat0: number,
  beat1: number,
  heightPx: number,
  opts: LodOptions = {},
): LodPlan {
  const [first, end] = indexRange(sorted, beat0, beat1);
  const count = end - first;
  const threshold = opts.threshold ?? 1;
  const binPxWanted = Math.max(1, opts.binPx ?? 4);
  // A bin of binPx rows can only be crowded if the whole range holds more than threshold * binPx items.
  if (count <= threshold * binPxWanted || heightPx <= 0 || !(beat1 > beat0)) return { first, end, bands: null };
  const nBins = Math.max(1, Math.ceil(heightPx / binPxWanted));
  const bins = densityBins(sorted, beat0, beat1, nBins);
  const binPx = heightPx / nBins;
  const crowded = new Uint8Array(nBins);
  let any = false;
  for (let i = 0; i < nBins; i++) {
    if ((bins[i] ?? 0) / binPx > threshold) {
      crowded[i] = 1;
      any = true;
    }
  }
  if (!any) return { first, end, bands: null };
  return {
    first,
    end,
    bands: { bins, beat0, binBeats: (beat1 - beat0) / nBins, binPx, crowded, peakPerRow: binMax(bins) / binPx },
  };
}

/** Bin index of a beat in `bands` (clamped), for skipping items of crowded bins. */
export function binOfBeat(bands: LodBands, beat: number): number {
  const i = Math.floor((beat - bands.beat0) / bands.binBeats);
  return Math.min(bands.bins.length - 1, Math.max(0, i));
}

/** True when the item at `beat` falls in a crowded bin (draw the band, not the item). */
export function isCrowded(plan: LodPlan, beat: number): boolean {
  return plan.bands !== null && plan.bands.crowded[binOfBeat(plan.bands, beat)] === 1;
}

/** Min and max of `values[i0..i1)` (clamped to the array); null for an empty slice. */
export function minMaxOf(
  min: ArrayLike<number>,
  max: ArrayLike<number>,
  i0: number,
  i1: number,
): readonly [number, number] | null {
  const a = Math.max(0, Math.floor(i0));
  const b = Math.min(min.length, max.length, Math.ceil(i1));
  if (b <= a) return null;
  let lo = Number.POSITIVE_INFINITY;
  let hi = Number.NEGATIVE_INFINITY;
  for (let i = a; i < b; i++) {
    const l = min[i] ?? 0;
    const h = max[i] ?? 0;
    if (l < lo) lo = l;
    if (h > hi) hi = h;
  }
  return [lo, hi];
}
